// Share pages (/c/, /w/, /l/) arrive server-rendered by the API (weavewords-api
// ShareRenderHandler — see its doc comment for the same contract from that side):
//   - its head tags carry data-ssr; the shell's generic tags wait in <template id="ssr-shell-head">
//   - the public DTO the page was built from is in <script id="ssr-data" data-path="/w/<token>">
//
// adoptSsrPage runs once in index.tsx, before React mounts. It keeps the DTO and removes the
// server's head tags; crawlers and link previews have read the server's HTML by then. The page
// then renders the same tags itself (<ShareHead>, same formulas as the server), so from React's
// first commit every page-specific head tag is React's own. Nothing removes a tag behind React's
// back later: React 19 adopts an existing <title> as its own hoisted node, and deleting one it
// adopted crashes it on unmount.
//
// The shell's generic tags are kept aside rather than restored at once — the share page renders
// all of them itself (its error screen renders a not-found title and noindex), and restoring both
// sets would leave crawlers that run JS with two descriptions and two og:titles, the generic one
// first. leaveSsrPage puts them back (inserting only, never removing) once the visitor navigates
// elsewhere, so later pages look as they would have without server rendering.
//
// Why not a default <Helmet> in App that page Helmets override instead: with React 19,
// react-helmet-async 3 renders every <Helmet> as its own native hoisted tags and doesn't merge
// instances, so a default one would add a second title and description to every page.

const normalize = (path: string) => path.replace(/\/+$/, '') || '/';

const safeDecode = (segment: string) => {
    try {
        return decodeURIComponent(segment);
    } catch {
        return segment;
    }
};

// The server builds data-path from the fully decoded URL path (as gin and useParams decode the
// token), so location.pathname is decoded segment by segment to compare — decodeURI would leave
// %2B, %3A, %40 and the like encoded.
const decodePath = (path: string) => path.split('/').map(safeDecode).join('/');

let embedded: { path: string; data: unknown } | null = null;
let shellHead: DocumentFragment | null = null;
let ssrPath: string | null = null;

export function adoptSsrPage(): void {
    const el = document.getElementById('ssr-data');
    const path = el?.getAttribute('data-path');
    if (el && path) {
        try {
            embedded = { path: normalize(path), data: JSON.parse(el.textContent ?? '') };
        } catch {
            embedded = null;
        }
    }
    // Removed whether or not the shell had generic tags to stash: a leftover server canonical or
    // noindex would otherwise duplicate the page's own and outlive it on later pages.
    document.querySelectorAll('[data-ssr]').forEach((node) => node.remove());
    const stash = document.getElementById('ssr-shell-head') as HTMLTemplateElement | null;
    if (!stash) return;
    ssrPath = normalize(decodePath(window.location.pathname));
    shellHead = stash.content;
    stash.remove();
}

// The DTO the server embedded for `path`, or null (another page, a lookup error, a legacy
// collection, or the visitor has since navigated away). `path` is built from the decoded route
// param, like the server's data-path.
export function ssrDataFor<T>(path: string): T | null {
    return embedded && embedded.path === normalize(path) ? (embedded.data as T) : null;
}

// Called on every route change with location.pathname. Once the visitor leaves the
// server-rendered page, its embedded DTO is dropped (coming back fetches fresh data) and the
// shell's generic head tags go back in — the generic <title> too, as the fallback for screens
// that set none (e.g. a loading state). It's appended after any title the next page already
// rendered, so that one stays document.title; only React ever removes either.
export function leaveSsrPage(currentPath: string): void {
    const current = normalize(decodePath(currentPath));
    if (embedded && embedded.path !== current) embedded = null;
    if (!shellHead || current === ssrPath) return;
    const tags = shellHead;
    shellHead = null;
    document.head.appendChild(tags);
}

// For tests.
export function resetSsrData(): void {
    embedded = null;
    shellHead = null;
    ssrPath = null;
}
